import { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Grid, Card, CardContent, Alert, CircularProgress, InputAdornment, Divider,
} from '@mui/material';
import { ArrowLeft, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { bookingApi, roomApi, roomTypeApi, Room, RoomType, CreateBookingDto, getEffectiveRates } from '../../services/hotelApi';

const BookingForm = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isFr = language === 'fr';
  const currency = user?.company?.signCurrency || '$';
  const basePath = `/${language}/hotel-dashboard`;

  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    roomId: '',
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    cardNumber: '',
    nomFemme: '',
    checkIn: '',
    checkOut: '',
    rateType: 'standard' as 'standard' | 'negotiated',
    negotiatedRate: '',
    paidAmount: '',
    paymentMethod: '' as string,
    notes: '',
  });

  const t = isFr ? {
    title: 'Nouvelle Réservation',
    subtitle: 'Créer une nouvelle réservation',
    back: 'Retour',
    clientInfo: 'Informations du client',
    clientName: 'Nom du client',
    clientPhone: 'Téléphone',
    clientEmail: 'Email',
    cardNumber: 'N° Carte',
    nomFemme: 'Nom de l\'épouse',
    bookingDetails: 'Détails de la réservation',
    room: 'Chambre',
    checkIn: 'Date d\'arrivée',
    checkOut: 'Date de départ (optionnel)',
    rateType: 'Type de tarif',
    standard: 'Standard',
    negotiated: 'Négocié',
    negotiatedRate: 'Tarif négocié',
    payment: 'Paiement initial (optionnel)',
    paidAmount: 'Montant payé',
    paymentMethod: 'Méthode de paiement',
    cash: 'Espèces',
    mobile_money: 'Mobile Money',
    bank_transfer: 'Virement',
    card: 'Carte',
    notes: 'Notes',
    save: 'Enregistrer',
    roomRates: 'Tarifs de la chambre',
    hourly: 'Heure',
    night: 'Nuit',
    fullDay: 'Journée',
    available: 'Disponible',
    occupied: 'Occupée',
    selectRoom: 'Sélectionnez une chambre',
    noAvailableRooms: 'Aucune chambre disponible',
    requiredField: 'Ce champ est requis',
    errorCreating: 'Erreur lors de la création',
  } : {
    title: 'New Booking',
    subtitle: 'Create a new booking',
    back: 'Back',
    clientInfo: 'Client Information',
    clientName: 'Client Name',
    clientPhone: 'Phone',
    clientEmail: 'Email',
    cardNumber: 'Card Number',
    nomFemme: 'Spouse Name',
    bookingDetails: 'Booking Details',
    room: 'Room',
    checkIn: 'Check-In Date',
    checkOut: 'Check-Out Date (optional)',
    rateType: 'Rate Type',
    standard: 'Standard',
    negotiated: 'Negotiated',
    negotiatedRate: 'Negotiated Rate',
    payment: 'Initial Payment (optional)',
    paidAmount: 'Amount Paid',
    paymentMethod: 'Payment Method',
    cash: 'Cash',
    mobile_money: 'Mobile Money',
    bank_transfer: 'Bank Transfer',
    card: 'Card',
    notes: 'Notes',
    save: 'Save',
    roomRates: 'Room Rates',
    hourly: 'Hourly',
    night: 'Night',
    fullDay: 'Full Day',
    available: 'Available',
    occupied: 'Occupied',
    selectRoom: 'Select a room',
    noAvailableRooms: 'No available rooms',
    requiredField: 'This field is required',
    errorCreating: 'Error creating booking',
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [r, rt] = await Promise.all([roomApi.getAll(), roomTypeApi.getAll()]);
        setRooms(Array.isArray(r) ? r : []);
        setRoomTypes(Array.isArray(rt) ? rt : []);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const selectedRoom = rooms.find(r => r._id === form.roomId);
  const rates = selectedRoom ? getEffectiveRates(selectedRoom) : null;

  const handleSubmit = async () => {
    if (!form.roomId || !form.clientName || !form.checkIn) {
      setError(t.requiredField);
      return;
    }
    try {
      setSaving(true);
      setError('');
      const dto: CreateBookingDto = {
        roomId: form.roomId,
        clientName: form.clientName,
        clientPhone: form.clientPhone || undefined,
        clientEmail: form.clientEmail || undefined,
        cardNumber: form.cardNumber || undefined,
        nomFemme: form.nomFemme || undefined,
        checkIn: new Date(form.checkIn).toISOString(),
        checkOut: form.checkOut ? new Date(form.checkOut).toISOString() : undefined,
        rateType: form.rateType,
        negotiatedRate: form.rateType === 'negotiated' && form.negotiatedRate ? Number(form.negotiatedRate) : undefined,
        paidAmount: form.paidAmount ? Number(form.paidAmount) : undefined,
        paymentMethod: form.paymentMethod as any || undefined,
        notes: form.notes || undefined,
      };
      await bookingApi.create(dto);
      navigate(`${basePath}/${isFr ? 'reservations' : 'bookings'}`);
    } catch (err: any) {
      setError(err.message || t.errorCreating);
    } finally {
      setSaving(false);
    }
  };

  const availableRooms = rooms.filter(r => r.status === 'available');

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowLeft size={18} />} onClick={() => navigate(-1)}
          sx={{ textTransform: 'none', color: '#0D3C4C' }}>{t.back}</Button>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D3C4C' }}>{t.title}</Typography>
          <Typography variant="body1" color="text.secondary">{t.subtitle}</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Client Info */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{t.clientInfo}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={t.clientName} required value={form.clientName}
                onChange={e => setForm(p => ({ ...p, clientName: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={t.clientPhone} value={form.clientPhone}
                onChange={e => setForm(p => ({ ...p, clientPhone: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={t.clientEmail} type="email" value={form.clientEmail}
                onChange={e => setForm(p => ({ ...p, clientEmail: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={t.cardNumber} value={form.cardNumber}
                onChange={e => setForm(p => ({ ...p, cardNumber: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={t.nomFemme} value={form.nomFemme}
                onChange={e => setForm(p => ({ ...p, nomFemme: e.target.value }))} size="small" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Booking Details */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{t.bookingDetails}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>{t.room}</InputLabel>
                <Select value={form.roomId} label={t.room}
                  onChange={e => setForm(p => ({ ...p, roomId: e.target.value }))}>
                  {availableRooms.length === 0 && <MenuItem disabled>{t.noAvailableRooms}</MenuItem>}
                  {availableRooms.map(room => {
                    const typeName = typeof room.roomTypeId === 'object' ? (room.roomTypeId as RoomType).name : '';
                    return (
                      <MenuItem key={room._id} value={room._id}>
                        {room.roomNumber} {typeName ? `• ${typeName}` : ''} {room.floor ? `(${isFr ? 'Étage' : 'Floor'} ${room.floor})` : ''}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            {/* Show rates for selected room */}
            {rates && (
              <Grid item xs={12}>
                <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{t.roomRates}</Typography>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Typography variant="body2">{t.hourly}: <strong>{rates.hourlyRate.toLocaleString()} {currency}</strong></Typography>
                    <Typography variant="body2">{t.night}: <strong>{rates.nightRate.toLocaleString()} {currency}</strong></Typography>
                    <Typography variant="body2">{t.fullDay}: <strong>{rates.fullDayRate.toLocaleString()} {currency}</strong></Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={t.checkIn} type="datetime-local" required value={form.checkIn}
                onChange={e => setForm(p => ({ ...p, checkIn: e.target.value }))} size="small"
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={t.checkOut} type="datetime-local" value={form.checkOut}
                onChange={e => setForm(p => ({ ...p, checkOut: e.target.value }))} size="small"
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t.rateType}</InputLabel>
                <Select value={form.rateType} label={t.rateType}
                  onChange={e => setForm(p => ({ ...p, rateType: e.target.value as any }))}>
                  <MenuItem value="standard">{t.standard}</MenuItem>
                  <MenuItem value="negotiated">{t.negotiated}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {form.rateType === 'negotiated' && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t.negotiatedRate} type="number" value={form.negotiatedRate}
                  onChange={e => setForm(p => ({ ...p, negotiatedRate: e.target.value }))} size="small"
                  InputProps={{ endAdornment: <InputAdornment position="end">{currency}</InputAdornment> }} />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth label={t.notes} value={form.notes} multiline rows={2}
                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} size="small" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payment */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{t.payment}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={t.paidAmount} type="number" value={form.paidAmount}
                onChange={e => setForm(p => ({ ...p, paidAmount: e.target.value }))} size="small"
                InputProps={{ endAdornment: <InputAdornment position="end">{currency}</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t.paymentMethod}</InputLabel>
                <Select value={form.paymentMethod} label={t.paymentMethod}
                  onChange={e => setForm(p => ({ ...p, paymentMethod: e.target.value }))}>
                  <MenuItem value="">&nbsp;</MenuItem>
                  <MenuItem value="cash">{t.cash}</MenuItem>
                  <MenuItem value="mobile_money">{t.mobile_money}</MenuItem>
                  <MenuItem value="bank_transfer">{t.bank_transfer}</MenuItem>
                  <MenuItem value="card">{t.card}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save size={18} />}
          onClick={handleSubmit} disabled={saving || !form.roomId || !form.clientName || !form.checkIn}
          sx={{ backgroundColor: '#0D3C4C', textTransform: 'none', px: 4, '&:hover': { backgroundColor: '#164B61' } }}>
          {t.save}
        </Button>
      </Box>
    </Container>
  );
};

export default BookingForm;
